import { useState } from "react";
import { LogIn, LogOut } from "lucide-react";
import { useAuth } from "@/auth/AuthProvider";

/**
 * Compact account widget for the app header. Anonymous users see a
 * small "Sign in" button; authenticated users see their avatar/initial
 * with a click-to-sign-out menu.
 */
export default function AccountBadge() {
  const { user, loading, signIn, signOut } = useAuth();
  const [open, setOpen] = useState(false);

  if (loading) {
    return (
      <span
        data-testid="account-loading"
        className="text-[10px] uppercase tracking-[0.3em] text-sumiSoft font-sans px-2"
      >
        …
      </span>
    );
  }

  if (!user) {
    return (
      <button
        type="button"
        onClick={signIn}
        data-testid="account-sign-in"
        className="inline-flex items-center gap-2 px-3 py-1.5 text-[11px] uppercase tracking-[0.25em] font-sans text-sumi border border-borderDefault hover:border-shu hover:text-shu transition-colors"
      >
        <LogIn size={12} strokeWidth={1.8} />
        Sign in
      </button>
    );
  }

  const initial = (user.name || user.email || "?").trim().charAt(0).toUpperCase();
  return (
    <div className="relative" data-testid="account-badge">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="account-open"
        className="inline-flex items-center gap-2 pl-1 pr-3 py-1 border border-borderDefault hover:border-shu transition-colors"
        title={user.email}
      >
        {user.picture ? (
          <img
            src={user.picture}
            alt=""
            className="w-7 h-7 rounded-full object-cover"
          />
        ) : (
          <span className="w-7 h-7 rounded-full bg-sumi text-washi flex items-center justify-center font-serif text-sm">
            {initial}
          </span>
        )}
        <span className="font-sans text-xs text-sumi truncate max-w-[120px]">
          {user.name || user.email}
        </span>
      </button>
      {open && (
        <>
          <div
            aria-hidden
            className="fixed inset-0 z-30"
            onClick={() => setOpen(false)}
          />
          <div
            data-testid="account-menu"
            className="absolute right-0 top-full mt-1 w-56 bg-washi border border-borderDefault shadow-lg z-40"
          >
            <p className="px-3 py-2 text-[10px] uppercase tracking-[0.3em] text-sumiSoft font-sans border-b border-borderDefault truncate">
              {user.email}
            </p>
            <button
              type="button"
              onClick={signOut}
              data-testid="account-sign-out"
              className="w-full px-3 py-2 text-left font-sans text-xs text-sumi hover:bg-shu/5 flex items-center gap-2"
            >
              <LogOut size={12} strokeWidth={1.8} />
              Sign out
            </button>
          </div>
        </>
      )}
    </div>
  );
}
